import { useMemo } from "react"
import { ComposedChart } from "~/ui-rtk/components/ui/charts"
import ChartLoader from "~/ui-rtk/components/ui/charts/ChartLoader"
import { Card } from "~/ui-rtk/components/ui/common"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import {
  AggregationType,
  VISUALIZATIONS_MAP,
  VISUALIZATION_TYPE,
} from "~/ui-rtk/constants/charts"
import { type TGraphProps } from "~/ui-rtk/components/ui/charts/ComposedChart/ComposedChart"
import useSources from "~/ui-rtk/hooks/sources"

export const WIDGET = "mentions_analysis_time_series_chart"

export default function MentionsTimeSeriesChart({
  granularity,
  dateRange,
  compareRange,
  selectedMetrics,
  graphs,
}: {
  granularity: AggregationType
  dateRange: [Date, Date]
  compareRange?: [Date, Date]
  selectedMetrics: TMultiSelectOption[]
  graphs: TGraphProps[]
}) {
  const visualization = VISUALIZATIONS_MAP[WIDGET]
  const { sources } = useSources()

  const selectedDataKeys = useMemo(
    () => selectedMetrics.map(({ value }) => value),
    [selectedMetrics],
  )

  const selectedGraphs = useMemo(
    () => graphs.filter(graph => selectedDataKeys.includes(graph.dataKey)),
    [graphs, selectedDataKeys],
  )

  return (
    <Card
      rounded
      className="bg-system-blue-800 h-full p-system-sm rounded-system-md"
      role="region"
      aria-label="Mentions time series chart"
    >
      <ChartLoader
        chartId={WIDGET}
        className="border-0 h-full pb-0"
        widgetType={VISUALIZATION_TYPE.ComposedChart}
        component={ComposedChart}
        dateRange={dateRange}
        compareRange={compareRange ?? undefined}
        key={WIDGET}
        granularity={granularity}
        sources={sources}
        enableCSVExport={true}
        csvExportFilename={WIDGET || "mentions-analysis"}
        enableChartDataExport={true}
        chartDataExportFilename="mentions-analysis-chart-data"
        hideAggSelector={true}
        requiredSources={visualization?.requiredSources}
        cubeQueryParams={{
          [WIDGET]: {
            granularity,
          },
        }}
        description={visualization?.description ?? undefined}
        compareMode={
          visualization?.props?.comparisonEnabled &&
          (compareRange?.length ?? 0) > 0
        }
        {...visualization?.props}
        graphs={selectedGraphs}
      />
    </Card>
  )
}
